/* ══════════════════════════════════════════════════════════════════
   IndexedDB — der Platz auf dem Gerät.

   Drei Fächer, mehr nicht:

   | `warteschlange` | was draußen entstanden ist und noch hinaus muss |
   | `mappe`         | was zuletzt hereinkam, damit im Funkloch etwas dasteht |
   | `entwurf`       | was halb eingetragen ist und noch nicht abgeschlossen (`entwurf.ts`) |

   **Der Entwurf liegt in einer eigenen Datenbank** (17.09.2026). Ein drittes
   Fach in `machine-office` hieße eine neue Fassung dieser Datenbank — und
   einen Versionswechsel, den jeder noch offene Reiter mit der alten Fassung
   blockiert. Der neue Reiter bekäme dann gar keinen Speicher, auch keine
   Warteschlange: ein Protokoll im Funkloch wäre verloren, damit ein Zählerstand
   einen Wechsel der Leiste überlebt. Eine zweite Datenbank kostet nichts davon.

   **Warum IndexedDB und nicht `localStorage`.** Fotos. Ein Übergabeprotokoll
   trägt fünf Bilder und eine Unterschrift; als Text kodiert wären das
   mehrere Megabyte in einem Speicher, der synchron arbeitet und bei fünf
   Megabyte aufhört. IndexedDB nimmt `Blob` so, wie die Kamera ihn liefert.

   **Warum eine eigene kleine Hülle und keine Bibliothek.** Was gebraucht
   wird, sind vier Wörter — lesen, schreiben, löschen, alle. `idb` wäre
   dafür eine Abhängigkeit im Bündel, das ein Fahrer über Mobilfunk lädt.

   **Alles hier darf fehlschlagen.** Ein privates Fenster, ein Browser mit
   gesperrtem Speicher, ein volles Gerät — dann gibt es eben keinen Vorrat.
   Jede Funktion fängt das ab und meldet „nichts da", statt die Maske
   mitzureißen. Was daraus folgt, sagt der Schirm an der Stelle, an der es
   zählt: ohne Speicher kein Aufnehmen im Funkloch.
   ══════════════════════════════════════════════════════════════════ */

const DATENBANK = "machine-office";
const FASSUNG = 1;

export const FACH_WARTESCHLANGE = "warteschlange";
export const FACH_MAPPE = "mappe";
export const FACH_ENTWURF = "entwurf";

type Datenbank = { name: string; fassung: number; faecher: { fach: string; schluessel: string }[] };

const HAUPT: Datenbank = {
  name: DATENBANK,
  fassung: FASSUNG,
  faecher: [
    // `id` ist die im Browser erzeugte UUID — dieselbe, die der Server als
    // `erfassung_id` sieht (Migration 0113).
    { fach: FACH_WARTESCHLANGE, schluessel: "id" },
    { fach: FACH_MAPPE, schluessel: "weg" },
  ],
};

/** Die Entwürfe — eine eigene Datenbank, siehe oben. */
const ENTWUERFE: Datenbank = {
  name: `${DATENBANK}-entwurf`,
  fassung: 1,
  faecher: [{ fach: FACH_ENTWURF, schluessel: "schluessel" }],
};

const offen = new Map<string, Promise<IDBDatabase | null>>();

function aufmachen(bank: Datenbank): Promise<IDBDatabase | null> {
  const schon = offen.get(bank.name);
  if (schon) return schon;
  const neu = new Promise<IDBDatabase | null>((fertig) => {
    if (typeof indexedDB === "undefined") return fertig(null);
    let anfrage: IDBOpenDBRequest;
    try {
      anfrage = indexedDB.open(bank.name, bank.fassung);
    } catch {
      return fertig(null);
    }
    anfrage.onupgradeneeded = () => {
      const db = anfrage.result;
      for (const { fach, schluessel } of bank.faecher) {
        if (!db.objectStoreNames.contains(fach)) db.createObjectStore(fach, { keyPath: schluessel });
      }
    };
    anfrage.onsuccess = () => fertig(anfrage.result);
    anfrage.onerror = () => fertig(null);
    anfrage.onblocked = () => fertig(null);
  });
  offen.set(bank.name, neu);
  return neu;
}

function warten<T>(anfrage: IDBRequest<T>): Promise<T> {
  return new Promise((fertig, scheitern) => {
    anfrage.onsuccess = () => fertig(anfrage.result);
    anfrage.onerror = () => scheitern(anfrage.error ?? new Error("IndexedDB"));
  });
}

async function mitFach<T>(
  fach: string,
  art: IDBTransactionMode,
  arbeit: (laden: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  const db = await aufmachen(fach === FACH_ENTWURF ? ENTWUERFE : HAUPT);
  if (!db) return null;
  try {
    const zug = db.transaction(fach, art);
    /* **Die Hörer hängen dran, bevor gewartet wird.** Ein Zug, der fertig ist,
       bevor jemand zuhört, ruft niemanden mehr — und dann wartet das Telefon
       für immer auf ein Speichern, das längst durch ist. Genau so hing am
       16.09.2026 der Knopf „Einstempeln" auf „Einen Moment …". */
    const durch = art === "readwrite"
      ? new Promise<void>((fertig) => {
        zug.oncomplete = () => fertig();
        zug.onerror = () => fertig();
        zug.onabort = () => fertig();
      })
      : Promise.resolve();
    const ergebnis = await warten(arbeit(zug.objectStore(fach)));
    await durch;
    return ergebnis;
  } catch {
    return null;
  }
}

export const lesen = <T>(fach: string, schluessel: string): Promise<T | null> =>
  mitFach<T>(fach, "readonly", (l) => l.get(schluessel) as IDBRequest<T>);

export const alle = async <T>(fach: string): Promise<T[]> =>
  (await mitFach<T[]>(fach, "readonly", (l) => l.getAll() as IDBRequest<T[]>)) ?? [];

export const schreiben = async (fach: string, wert: unknown): Promise<boolean> =>
  (await mitFach(fach, "readwrite", (l) => l.put(wert) as IDBRequest<IDBValidKey>)) !== null;

export const entfernen = async (fach: string, schluessel: string): Promise<void> => {
  await mitFach(fach, "readwrite", (l) => l.delete(schluessel) as IDBRequest<undefined>);
};

/**
 * Eine UUID vom Gerät.
 *
 * `crypto.randomUUID` gibt es nur in einem sicheren Zusammenhang — also unter
 * `https` und auf `localhost`. Genau dort läuft diese Anwendung; der Rückweg
 * darunter ist trotzdem gebaut, weil eine fehlende UUID hier nicht „ein
 * Feld ist leer" hieße, sondern „die Eintragung lässt sich nicht aufnehmen".
 */
export function uuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  // Fassung 4, Variante 1 — sonst weist die API sie als kein UUID ab.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
